import { useState, useEffect, useRef, useCallback } from "react";

// ── API helper (calls our Express backend) ─────────────────
const API_BASE = '/api';

async function chatWithNova({ system, messages, model, max_tokens }) {
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ system, messages, model, max_tokens }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || `HTTP ${res.status}`);
  return data;
}

async function getSessions() {
  const res = await fetch(`${API_BASE}/sessions`);
  return res.ok ? res.json() : [];
}

async function saveSessionAPI(session) {
  await fetch(`${API_BASE}/sessions/${session.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(session),
  });
}

async function deleteSessionAPI(id) {
  await fetch(`${API_BASE}/sessions/${id}`, { method: 'DELETE' });
}

// ── Modes ─────────────────────────────────────────────────
const MODES = {
  general: {
    name: "NOVA", subtitle: "General Intelligence", icon: "◈", accent: "#00d4ff",
    system: `You are NOVA, a highly advanced AI assistant with exceptional intelligence, creativity, and emotional awareness. You engage in natural, human-like conversations. You provide thorough, insightful, and helpful responses. Format code in markdown code blocks with language labels. Use **bold** for emphasis, ## for section headers, and - for bullet lists when appropriate.`
  },
  code: {
    name: "DEVX", subtitle: "Code Intelligence", icon: "⌥", accent: "#a855f7",
    system: `You are DEVX, an elite software engineering AI. You write clean, optimized, well-documented code. You excel at debugging, code review, architecture design, and explaining complex programming concepts. Always use proper markdown code blocks with language labels. Explain your code clearly. Suggest best practices and potential improvements.`
  },
  cybersec: {
    name: "SHIELD", subtitle: "Security Intelligence", icon: "⬡", accent: "#ef4444",
    system: `You are SHIELD, a cybersecurity expert AI. You help with security concepts, CTF challenges, penetration testing education, vulnerability analysis, secure coding practices, threat modeling, and defensive security strategies. You strictly operate within ethical and legal boundaries.`
  },
  tutor: {
    name: "SAGE", subtitle: "Learning Intelligence", icon: "◎", accent: "#10b981",
    system: `You are SAGE, a brilliant and patient AI tutor. You explain complex topics with clarity and depth, using analogies, real-world examples, and step-by-step breakdowns. You adapt to the learner's level, encourage critical thinking, and make learning genuinely enjoyable.`
  },
  creative: {
    name: "MUSE", subtitle: "Creative Intelligence", icon: "✦", accent: "#f59e0b",
    system: `You are MUSE, a highly creative AI with exceptional skills in storytelling, poetry, screenwriting, marketing copy, worldbuilding, and creative ideation. You bring imagination, style, and artistic sensibility to every response.`
  }
};

const QUICK_PROMPTS = [
  { text: "Explain quantum computing simply", icon: "⚛" },
  { text: "Write a Python web scraper", icon: "🐍" },
  { text: "Debug my JavaScript code", icon: "🔧" },
  { text: "Create a blog post outline", icon: "✍" },
  { text: "How do neural networks work?", icon: "🧠" },
  { text: "Explain RSA encryption", icon: "🔐" },
];

// ── CSS ───────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Space+Grotesk:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg0: #030812; --bg1: #060e1f; --bg2: #0a1428;
    --glass: rgba(255,255,255,0.035); --glass2: rgba(255,255,255,0.06); --glass3: rgba(255,255,255,0.09);
    --border: rgba(255,255,255,0.07); --border2: rgba(255,255,255,0.13);
    --text: #e8f0ff; --text2: #8899bb; --text3: #445577;
    --cyan: #00d4ff; --purple: #a855f7; --emerald: #10b981; --amber: #f59e0b; --red: #ef4444;
  }
  html, body { height: 100%; background: var(--bg0); }
  .nova-root { font-family: 'Space Grotesk', sans-serif; background: var(--bg0); color: var(--text); height: 100vh; display: flex; overflow: hidden; position: relative; }
  .orb { position: fixed; border-radius: 50%; filter: blur(90px); opacity: 0.12; pointer-events: none; z-index: 0; animation: orbFloat 10s ease-in-out infinite; }
  @keyframes orbFloat { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-40px) scale(1.08); } }
  @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes ripple { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(2); opacity: 0; } }
  @keyframes glow { 0%,100% { box-shadow: 0 0 8px currentColor; } 50% { box-shadow: 0 0 20px currentColor; } }
  .msg-enter { animation: fadeSlideUp 0.35s cubic-bezier(0.22,1,0.36,1) forwards; }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
  pre { background: rgba(0,0,0,0.5) !important; border: 1px solid rgba(255,255,255,0.08) !important; border-radius: 10px !important; padding: 16px !important; overflow-x: auto !important; font-family: 'JetBrains Mono', monospace !important; font-size: 12.5px !important; line-height: 1.65 !important; margin: 10px 0 !important; }
  code { font-family: 'JetBrains Mono', monospace; font-size: 0.85em; background: rgba(0,212,255,0.1); color: #00d4ff; padding: 2px 6px; border-radius: 4px; }
  pre code { background: none; padding: 0; color: #cdd6f4; font-size: 1em; }
  textarea { font-family: 'Space Grotesk', sans-serif; }
  textarea::placeholder { color: var(--text3) !important; }
  textarea:focus { outline: none; }
  .cursor { display: inline-block; width: 2px; height: 1em; background: var(--cyan); margin-left: 1px; vertical-align: text-bottom; animation: pulse 0.7s ease infinite; }
  .dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--cyan); margin: 0 2px; animation: pulse 1.2s ease infinite; }
  .dot:nth-child(2) { animation-delay: 0.2s; background: var(--purple); }
  .dot:nth-child(3) { animation-delay: 0.4s; background: var(--emerald); }
  .sidebar-btn { width: 100%; padding: 8px 10px; margin-bottom: 3px; background: transparent; border: 1px solid transparent; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 9px; font-family: 'Space Grotesk', sans-serif; transition: all 0.18s ease; text-align: left; }
  .sidebar-btn:hover { background: var(--glass2); border-color: var(--border); }
  .quick-btn { padding: 11px 14px; text-align: left; background: var(--glass); border: 1px solid var(--border); border-radius: 12px; cursor: pointer; color: var(--text2); font-size: 12.5px; font-family: 'Space Grotesk', sans-serif; display: flex; align-items: flex-start; gap: 8px; transition: all 0.18s ease; line-height: 1.4; }
  .quick-btn:hover { background: var(--glass3); color: var(--text); border-color: var(--border2); transform: translateY(-1px); }
  .send-btn { transition: all 0.18s ease; }
  .send-btn:hover:not(:disabled) { transform: scale(1.05); }
  .send-btn:active:not(:disabled) { transform: scale(0.96); }
  .mode-chip { transition: all 0.18s ease; }
  .mode-chip:hover { transform: translateY(-1px); }
  .status-bar { font-size: 10px; padding: 3px 10px; border-radius: 20px; display: inline-flex; align-items: center; gap: 5px; }
`;

// ── Markdown parser ───────────────────────────────────────
function parseMarkdown(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim() || 'code';
      let code = '';
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { code += (code ? '\n' : '') + lines[i]; i++; }
      blocks.push({ type: 'code', lang, content: code }); i++; continue;
    }
    if (line.startsWith('### ')) blocks.push({ type: 'h3', content: line.slice(4) });
    else if (line.startsWith('## ')) blocks.push({ type: 'h2', content: line.slice(3) });
    else if (line.startsWith('# ')) blocks.push({ type: 'h1', content: line.slice(2) });
    else if (/^[-*] /.test(line)) blocks.push({ type: 'li', content: line.slice(2) });
    else if (/^\d+\. /.test(line)) blocks.push({ type: 'oli', num: parseInt(line.match(/^(\d+)/)[1], 10), content: line.replace(/^\d+\. /, '') });
    else if (line.trim() === '') blocks.push({ type: 'spacer' });
    else blocks.push({ type: 'p', content: line });
    i++;
  }
  return blocks;
}

function inlineFmt(text) {
  if (!text) return '';
  let _k = 0;
  const parts = [];
  let rem = text;
  while (rem.length > 0) {
    const bold = rem.match(/^([\s\S]*?)\*\*([\s\S]*?)\*\*([\s\S]*)$/);
    const code = rem.match(/^([\s\S]*?)`([^`\n]+)`([\s\S]*)$/);
    const italic = rem.match(/^([\s\S]*?)_([^_\n]+)_([\s\S]*)$/);
    const bpos = bold ? bold[1].length : Infinity;
    const cpos = code ? code[1].length : Infinity;
    const ipos = italic ? italic[1].length : Infinity;
    const first = Math.min(bpos, cpos, ipos);
    if (first === Infinity) { parts.push(<span key={_k++}>{rem}</span>); break; }
    if (first === bpos && bold) { if (bold[1]) parts.push(<span key={_k++}>{bold[1]}</span>); parts.push(<strong key={_k++} style={{ color: '#f0f6ff', fontWeight: 600 }}>{bold[2]}</strong>); rem = bold[3]; }
    else if (first === cpos && code) { if (code[1]) parts.push(<span key={_k++}>{code[1]}</span>); parts.push(<code key={_k++}>{code[2]}</code>); rem = code[3]; }
    else if (first === ipos && italic) { if (italic[1]) parts.push(<span key={_k++}>{italic[1]}</span>); parts.push(<em key={_k++} style={{ color: '#9aadcc', fontStyle: 'italic' }}>{italic[2]}</em>); rem = italic[3]; }
    else { parts.push(<span key={_k++}>{rem}</span>); break; }
  }
  return parts;
}

function CodeBlock({ lang, content, accent }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <div style={{ position: 'relative', margin: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px 10px 0 0' }}>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: accent, letterSpacing: '0.06em' }}>{lang.toUpperCase()}</span>
        <button onClick={copy} style={{ background: 'none', border: 'none', color: copied ? '#10b981' : '#445577', cursor: 'pointer', fontSize: 11, fontFamily: 'Space Grotesk', padding: '2px 6px', borderRadius: 4, transition: 'color 0.2s' }}>{copied ? '✓ Copied' : '⎘ Copy'}</button>
      </div>
      <pre style={{ borderRadius: '0 0 10px 10px', marginTop: 0, borderTop: 'none' }}><code>{content}</code></pre>
    </div>
  );
}

function RenderBlocks({ blocks, accent, streaming }) {
  return (
    <>
      {blocks.map((bl, i) => {
        switch (bl.type) {
          case 'code': return <CodeBlock key={i} lang={bl.lang} content={bl.content} accent={accent} />;
          case 'h1': return <div key={i} style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Syne', color: '#f0f6ff', margin: '14px 0 6px' }}>{inlineFmt(bl.content)}</div>;
          case 'h2': return <div key={i} style={{ fontSize: 15, fontWeight: 700, fontFamily: 'Syne', color: '#d0dcf0', margin: '12px 0 4px' }}>{inlineFmt(bl.content)}</div>;
          case 'h3': return <div key={i} style={{ fontSize: 13, fontWeight: 600, color: accent, margin: '10px 0 3px', letterSpacing: '0.03em' }}>{inlineFmt(bl.content)}</div>;
          case 'li': return <div key={i} style={{ display: 'flex', gap: 8, margin: '3px 0', alignItems: 'flex-start' }}><span style={{ color: accent, marginTop: 3, fontSize: 10, flexShrink: 0 }}>▸</span><span style={{ color: '#b0c4de', fontSize: 13.5, lineHeight: 1.65 }}>{inlineFmt(bl.content)}</span></div>;
          case 'oli': return <div key={i} style={{ display: 'flex', gap: 8, margin: '3px 0', alignItems: 'flex-start' }}><span style={{ color: accent, fontWeight: 600, fontSize: 12, flexShrink: 0, minWidth: 16 }}>{bl.num}.</span><span style={{ color: '#b0c4de', fontSize: 13.5, lineHeight: 1.65 }}>{inlineFmt(bl.content)}</span></div>;
          case 'spacer': return <div key={i} style={{ height: 6 }} />;
          default: return <p key={i} style={{ color: '#b8cce0', fontSize: 13.5, lineHeight: 1.7, margin: '2px 0' }}>{inlineFmt(bl.content)}{streaming && i === blocks.length - 1 && <span className="cursor" />}</p>;
        }
      })}
    </>
  );
}

function Message({ msg, accent }) {
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === 'user';
  const blocks = parseMarkdown(msg.content || '');
  const modeAccent = msg.mode ? MODES[msg.mode]?.accent : accent;
  return (
    <div className="msg-enter" style={{ display: 'flex', gap: 10, flexDirection: isUser ? 'row-reverse' : 'row', alignItems: 'flex-start', marginBottom: 22 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: isUser ? 'rgba(168,85,247,0.18)' : `${modeAccent}18`, border: `1px solid ${isUser ? 'rgba(168,85,247,0.35)' : modeAccent + '35'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: isUser ? '#a855f7' : modeAccent }}>
        {isUser ? '✦' : (msg.mode ? MODES[msg.mode]?.icon : '◈')}
      </div>
      <div style={{ maxWidth: '82%', position: 'relative' }}>
        <div style={{ background: isUser ? 'rgba(168,85,247,0.12)' : 'var(--glass)', border: `1px solid ${isUser ? 'rgba(168,85,247,0.25)' : 'var(--border)'}`, borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px', padding: '12px 15px', backdropFilter: 'blur(14px)' }}>
          {isUser
            ? <p style={{ color: '#d8e8ff', fontSize: 13.5, lineHeight: 1.7 }}>{msg.content}</p>
            : <RenderBlocks blocks={blocks} accent={modeAccent} />
          }
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 5, justifyContent: isUser ? 'flex-end' : 'flex-start', opacity: 0.5 }}>
          <span style={{ fontSize: 10, color: '#2a3a55' }}>{new Date(msg.ts || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          {!isUser && (
            <button onClick={() => { navigator.clipboard.writeText(msg.content); setCopied(true); setTimeout(() => setCopied(false), 1500); }} style={{ background: 'none', border: 'none', color: copied ? '#10b981' : '#2a3a55', cursor: 'pointer', fontSize: 10, padding: 0, fontFamily: 'Space Grotesk', transition: 'color 0.2s' }}>{copied ? '✓' : '⎘'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function StreamingMessage({ text, accent, icon }) {
  const blocks = parseMarkdown(text || '');
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 22 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: `${accent}18`, border: `1px solid ${accent}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: accent }}>
        {text ? icon : <span className="dot" />}
        {!text && <><span className="dot" /><span className="dot" /></>}
      </div>
      <div style={{ maxWidth: '82%', background: 'var(--glass)', border: '1px solid var(--border)', borderRadius: '4px 16px 16px 16px', padding: '12px 15px', backdropFilter: 'blur(14px)' }}>
        {text ? <RenderBlocks blocks={blocks} accent={accent} streaming /> : <div style={{ height: 20, display: 'flex', alignItems: 'center' }}><span className="dot" /><span className="dot" /><span className="dot" /></div>}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────
export default function App() {
  const [mode, setMode] = useState('general');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(`s_${Date.now()}`);
  const [listening, setListening] = useState(false);
  const [voiceOut, setVoiceOut] = useState(false);
  const [welcome, setWelcome] = useState(true);
  const [rows, setRows] = useState(1);
  const [backendStatus, setBackendStatus] = useState('checking');

  const endRef = useRef(null);
  const inputRef = useRef(null);
  const recRef = useRef(null);
  const cur = MODES[mode];

  // Check backend health on mount
  useEffect(() => {
    fetch('/api/health')
      .then(r => r.ok ? setBackendStatus('online') : setBackendStatus('error'))
      .catch(() => setBackendStatus('offline'));
  }, []);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typingText]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    recRef.current = new SR();
    recRef.current.continuous = false;
    recRef.current.interimResults = false;
    recRef.current.onresult = (e) => { setInput(p => p + e.results[0][0].transcript); setListening(false); };
    recRef.current.onend = () => setListening(false);
    recRef.current.onerror = () => setListening(false);
  }, []);

  const loadSessions = async () => {
    try { setSessions(await getSessions()); } catch {}
  };

  const saveSession = async (sid, msgs, m) => {
    try {
      const preview = msgs.find(x => x.role === 'user')?.content?.slice(0, 55) || 'New Chat';
      await saveSessionAPI({ id: sid, title: preview, messages: msgs, mode: m, ts: Date.now() });
      loadSessions();
    } catch {}
  };

  const newChat = () => {
    const sid = `s_${Date.now()}`;
    setSessionId(sid); setMessages([]); setWelcome(true); setTypingText(''); setInput('');
  };

  const loadSess = (s) => { setSessionId(s.id); setMessages(s.messages); setMode(s.mode || 'general'); setWelcome(false); };

  const deleteSess = async (sid, e) => {
    e.stopPropagation();
    try { await deleteSessionAPI(sid); } catch {}
    if (sessionId === sid) newChat();
    loadSessions();
  };

  const toggleMic = () => {
    if (listening) { recRef.current?.stop(); setListening(false); }
    else { try { recRef.current?.start(); setListening(true); } catch {} }
  };

  const send = useCallback(async (txt = input) => {
    const text = (typeof txt === 'string' ? txt : input).trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text, id: `u${Date.now()}`, ts: Date.now() };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs); setInput(''); setRows(1); setLoading(true); setWelcome(false); setTypingText('');

    try {
      const apiMsgs = nextMsgs.slice(-40).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));
      const data = await chatWithNova({ system: cur.system, messages: apiMsgs, max_tokens: 2048 });
      const reply = data?.content?.[0]?.text || '(empty response)';

      let i = 0;
      const step = () => {
        if (i < reply.length) {
          i = Math.min(i + 4, reply.length);
          setTypingText(reply.slice(0, i));
          setTimeout(step, 7);
        } else {
          const aiMsg = { role: 'assistant', content: reply, id: `a${Date.now()}`, mode, ts: Date.now() };
          const final = [...nextMsgs, aiMsg];
          setMessages(final); setTypingText(''); setLoading(false);
          if (voiceOut) {
            const u = new SpeechSynthesisUtterance(reply.replace(/```[\s\S]*?```/g, 'code block').slice(0, 500));
            u.rate = 1.05; speechSynthesis.speak(u);
          }
          saveSession(sessionId, final, mode);
        }
      };
      step();
    } catch (err) {
      setMessages(p => [...p, { role: 'assistant', content: `⚠ Error: ${err.message}`, id: `e${Date.now()}`, isError: true, mode, ts: Date.now() }]);
      setTypingText(''); setLoading(false);
    }
  }, [input, loading, messages, mode, sessionId, voiceOut, cur]);

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const onInput = (e) => { setInput(e.target.value); setRows(Math.min(5, (e.target.value.match(/\n/g) || []).length + 1)); };

  const statusColor = { online: '#10b981', offline: '#ef4444', error: '#f59e0b', checking: '#8899bb' }[backendStatus];

  return (
    <>
      <style>{CSS}</style>
      <div className="orb" style={{ width: 500, height: 500, background: '#00d4ff', top: '-15%', left: '-10%' }} />
      <div className="orb" style={{ width: 420, height: 420, background: '#7c3aed', bottom: '-10%', right: '5%', animationDelay: '-4s' }} />
      <div className="orb" style={{ width: 280, height: 280, background: '#10b981', top: '45%', right: '-5%', animationDelay: '-7s' }} />
      <div className="orb" style={{ width: 200, height: 200, background: '#f59e0b', top: '15%', right: '25%', opacity: 0.07, animationDelay: '-2s' }} />

      <div className="nova-root">
        {/* ── SIDEBAR ── */}
        <div style={{ width: sidebarOpen ? 260 : 0, minWidth: sidebarOpen ? 260 : 0, transition: 'all 0.32s cubic-bezier(0.22,1,0.36,1)', overflow: 'hidden', background: 'rgba(3,8,18,0.85)', borderRight: '1px solid var(--border)', backdropFilter: 'blur(24px)', display: 'flex', flexDirection: 'column', zIndex: 10, flexShrink: 0 }}>
          
          {/* Logo */}
          <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(168,85,247,0.2))', border: '1px solid rgba(0,212,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#00d4ff' }}>◈</div>
              <div>
                <div style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 800, letterSpacing: '0.1em', color: '#f0f6ff' }}>NOVA AI</div>
                <div style={{ fontSize: 9, color: '#334466', letterSpacing: '0.15em' }}>INTELLIGENCE SUITE</div>
              </div>
            </div>
            
            {/* Backend status badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '4px 8px', background: 'var(--glass)', borderRadius: 6, border: '1px solid var(--border)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
              <span style={{ fontSize: 10, color: '#445577', letterSpacing: '0.1em' }}>
                BACKEND: <span style={{ color: statusColor }}>{backendStatus.toUpperCase()}</span>
              </span>
            </div>

            <button onClick={newChat} style={{ width: '100%', padding: '9px 12px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.22)', borderRadius: 9, color: '#00d4ff', fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Space Grotesk', fontWeight: 500, transition: 'all 0.18s' }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> New Conversation
            </button>
          </div>

          {/* Mode selector */}
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 9, color: '#334466', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 8 }}>AI PERSONALITY</div>
            {Object.entries(MODES).map(([k, m]) => (
              <button key={k} className="sidebar-btn" onClick={() => setMode(k)}
                style={{ background: mode === k ? `${m.accent}12` : 'transparent', border: `1px solid ${mode === k ? m.accent + '30' : 'transparent'}`, color: mode === k ? m.accent : '#5a7090' }}>
                <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{m.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, fontFamily: 'Syne', letterSpacing: '0.05em' }}>{m.name}</div>
                  <div style={{ fontSize: 10, opacity: 0.65 }}>{m.subtitle}</div>
                </div>
                {mode === k && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: m.accent, boxShadow: `0 0 6px ${m.accent}` }} />}
              </button>
            ))}
          </div>

          {/* Chat history */}
          <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}>
            <div style={{ fontSize: 9, color: '#334466', letterSpacing: '0.15em', fontWeight: 600, marginBottom: 8 }}>CONVERSATIONS</div>
            {sessions.length === 0 && <div style={{ fontSize: 12, color: '#2a3a55', textAlign: 'center', padding: '20px 0', lineHeight: 1.8 }}>No conversations yet.<br />Start a new chat above.</div>}
            {sessions.map(s => (
              <div key={s.id} onClick={() => loadSess(s)} style={{ padding: '8px 10px', borderRadius: 8, marginBottom: 3, background: sessionId === s.id ? 'rgba(255,255,255,0.055)' : 'transparent', border: `1px solid ${sessionId === s.id ? 'rgba(255,255,255,0.1)' : 'transparent'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.16s' }}>
                <span style={{ fontSize: 11, flexShrink: 0, color: MODES[s.mode || 'general']?.accent }}>{MODES[s.mode || 'general']?.icon}</span>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, color: '#8899bb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</div>
                  <div style={{ fontSize: 10, color: '#334466' }}>{new Date(s.ts).toLocaleDateString()}</div>
                </div>
                <button onClick={(e) => deleteSess(s.id, e)} style={{ background: 'none', border: 'none', color: '#2a3a55', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6 }}>
            <button onClick={() => setVoiceOut(!voiceOut)} style={{ flex: 1, padding: '7px 10px', background: voiceOut ? 'rgba(16,185,129,0.12)' : 'var(--glass)', border: `1px solid ${voiceOut ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`, borderRadius: 7, color: voiceOut ? '#10b981' : '#445577', fontSize: 11.5, cursor: 'pointer', fontFamily: 'Space Grotesk', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.18s' }}>
              🔊 <span>Voice {voiceOut ? 'ON' : 'OFF'}</span>
            </button>
          </div>
        </div>

        {/* ── MAIN AREA ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
          
          {/* Top bar */}
          <div style={{ padding: '11px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--border)', background: 'rgba(3,8,18,0.7)', backdropFilter: 'blur(20px)', flexShrink: 0 }}>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'var(--glass)', border: '1px solid var(--border)', borderRadius: 8, color: '#4a5a7a', cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>☰</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: cur.accent, boxShadow: `0 0 8px ${cur.accent}`, animation: loading ? 'pulse 0.8s ease infinite' : 'glow 2.5s ease infinite', color: cur.accent }} />
              <span style={{ fontFamily: 'Syne', fontSize: 14, fontWeight: 700, color: cur.accent, letterSpacing: '0.08em' }}>{cur.name}</span>
              <span style={{ fontSize: 11, color: '#334466' }}>{cur.subtitle}</span>
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
              {Object.entries(MODES).map(([k, m]) => (
                <button key={k} className="mode-chip" onClick={() => setMode(k)} title={m.name}
                  style={{ background: mode === k ? `${m.accent}18` : 'var(--glass)', border: `1px solid ${mode === k ? m.accent + '45' : 'var(--border)'}`, borderRadius: 7, cursor: 'pointer', padding: '5px 7px', fontSize: 13, lineHeight: 1 }}>
                  {m.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px 20px 16px' }}>
            <div style={{ maxWidth: 860, margin: '0 auto' }}>
              {welcome && messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '30px 10px 36px', animation: 'fadeIn 0.5s ease' }}>
                  <div style={{ position: 'relative', display: 'inline-block', marginBottom: 20 }}>
                    <div style={{ width: 80, height: 80, borderRadius: 22, background: `linear-gradient(135deg, ${cur.accent}22, rgba(168,85,247,0.15))`, border: `1px solid ${cur.accent}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, margin: '0 auto' }}>{cur.icon}</div>
                    <div style={{ position: 'absolute', inset: -4, borderRadius: 26, border: `1px solid ${cur.accent}18`, animation: 'ripple 3s ease infinite' }} />
                  </div>
                  <h1 style={{ fontFamily: 'Syne', fontSize: 30, fontWeight: 800, color: '#f0f6ff', marginBottom: 8, letterSpacing: '-0.01em' }}>
                    Hello, I'm <span style={{ color: cur.accent }}>{cur.name}</span>
                  </h1>
                  <p style={{ fontSize: 13.5, color: '#4a6080', marginBottom: 30, maxWidth: 400, margin: '0 auto 30px' }}>
                    {cur.subtitle} · Powered by Claude · Ask me anything
                  </p>
                  {backendStatus === 'offline' && (
                    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 16px', maxWidth: 480, margin: '0 auto 20px', fontSize: 12.5, color: '#ef4444', lineHeight: 1.6 }}>
                      ⚠ Backend is offline. Start it with <code style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>npm run dev</code> inside the <code style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>backend/</code> folder.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, maxWidth: 680, margin: '0 auto' }}>
                    {QUICK_PROMPTS.map((qp, i) => (
                      <button key={i} className="quick-btn" onClick={() => send(qp.text)}>
                        <span style={{ fontSize: 16, flexShrink: 0 }}>{qp.icon}</span>
                        <span>{qp.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map(m => <Message key={m.id} msg={m} accent={cur.accent} />)}
              {(loading || typingText) && <StreamingMessage text={typingText} accent={cur.accent} icon={cur.icon} />}
              <div ref={endRef} />
            </div>
          </div>

          {/* Input */}
          <div style={{ padding: '14px 18px 16px', background: 'rgba(3,8,18,0.8)', borderTop: '1px solid var(--border)', backdropFilter: 'blur(24px)', flexShrink: 0 }}>
            <div style={{ maxWidth: 860, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, background: 'var(--glass)', border: `1px solid ${input ? cur.accent + '35' : 'var(--border)'}`, borderRadius: 14, padding: '10px 12px', transition: 'border-color 0.2s', backdropFilter: 'blur(16px)' }}>
                <textarea ref={inputRef} value={input} onChange={onInput} onKeyDown={onKey} rows={rows} disabled={loading}
                  placeholder={`Message ${cur.name}… (Shift+Enter for new line)`}
                  style={{ flex: 1, background: 'none', border: 'none', color: '#e0ecff', fontSize: 13.5, lineHeight: 1.65, resize: 'none', maxHeight: 130, overflow: 'auto', fontFamily: 'Space Grotesk' }} />
                <button onClick={toggleMic} title="Voice input"
                  style={{ background: listening ? 'rgba(239,68,68,0.18)' : 'none', border: listening ? '1px solid rgba(239,68,68,0.35)' : 'none', borderRadius: 7, color: listening ? '#ef4444' : '#334466', cursor: 'pointer', fontSize: 16, padding: '4px 6px', flexShrink: 0, animation: listening ? 'pulse 0.8s ease infinite' : 'none' }}>🎤</button>
                <button className="send-btn" onClick={() => send()} disabled={!input.trim() || loading}
                  style={{ background: input.trim() && !loading ? `${cur.accent}18` : 'var(--glass)', border: `1px solid ${input.trim() && !loading ? cur.accent + '45' : 'var(--border)'}`, borderRadius: 9, cursor: input.trim() && !loading ? 'pointer' : 'not-allowed', color: input.trim() && !loading ? cur.accent : '#2a3a55', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15 }}>
                  {loading ? <div style={{ width: 14, height: 14, border: `2px solid ${cur.accent}30`, borderTop: `2px solid ${cur.accent}`, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> : '➤'}
                </button>
              </div>
              <div style={{ textAlign: 'center', marginTop: 7, fontSize: 10.5, color: '#1e2d44', letterSpacing: '0.05em' }}>
                NOVA AI · Express + Claude Sonnet · {sessions.length} conversations saved · Backend: <span style={{ color: statusColor }}>{backendStatus}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
